function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function nonEmptyRecord(value) {
  return Boolean(value && typeof value === "object" && Object.keys(value).length > 0);
}

function readStatus(present, loading, loadError) {
  if (loadError || loading) return "info";
  return present ? "recorded" : "missing";
}

function title(t, key, suffix = "") {
  return `${t(key)}${suffix ? ` · ${suffix}` : ""}`;
}

function listMissingFields(t, fields) {
  return t("eudamed.fields_missing").replace("{fields}", fields.join(", "));
}

function attachmentFor(deviceAttachments, recordId) {
  return deviceAttachments.find((item) => item.record_id === recordId) || null;
}

function attachmentItem({
  device,
  attachment,
  recordId,
  t,
  onDownload,
  onAction,
  attachmentsLoading,
  attachmentsError,
}) {
  const uploaded = Boolean(attachment);
  const reviewed = uploaded && attachment.review_status === "reviewed";
  const status = attachmentsLoading || attachmentsError
    ? "info"
    : uploaded
      ? (reviewed ? "recorded" : "review")
      : "missing";
  return {
    id: `${recordId}-${device.id}`,
    title: title(t, `eudamed.item.${recordId}.title`, device.name),
    detail: attachmentsError
      ? t("eudamed.attachments_error")
      : attachmentsLoading
        ? t("eudamed.attachments_loading")
        : uploaded
          ? `${attachment.file_name} · ${t(reviewed ? "eudamed.file_reviewed" : "eudamed.file_not_reviewed")}`
          : t(`eudamed.item.${recordId}.detail`),
    status,
    statusLabel: t(`eudamed.status.${status}`),
    actionLabel: uploaded || attachmentsLoading || attachmentsError ? "" : t("eudamed.open_section"),
    onAction: uploaded || attachmentsLoading || attachmentsError ? undefined : onAction,
    downloadLabel: t("eudamed.download"),
  onDownload: uploaded ? () => onDownload({
      id: `${recordId}-${device.id}`,
      type: "attachment",
      deviceId: device.id,
      attachmentId: attachment.id,
      fileName: attachment.file_name,
    }) : undefined,
  };
}

export function buildEudamedReadinessItems({
  t,
  roleView,
  profile,
  profileLoading = false,
  profileError = false,
  devices = [],
  version,
  attachmentsByDevice = {},
  attachmentsLoading = false,
  attachmentsError = false,
  onDownload = () => {},
  onOpenCompany = () => {},
  onOpenDevice = () => {},
  onOpenTab = () => {},
  onOpenProjectRole = () => {},
}) {
  const records = [];
  const deviceList = Array.isArray(devices) ? devices : [];
  const allAttachments = Object.values(attachmentsByDevice).flat();
  const profileReady = Boolean(
    profile
    && hasText(profile.company_name)
    && hasText(profile.company_address)
    && hasText(profile.company_country)
    && hasText(profile.economic_operator_type),
  );
  const missingCompanyFields = [
    !hasText(profile?.company_name) ? t("eudamed.field.company_name") : "",
    !hasText(profile?.company_address) ? t("eudamed.field.company_address") : "",
    !hasText(profile?.company_country) ? t("eudamed.field.company_country") : "",
    !hasText(profile?.economic_operator_type) ? t("eudamed.field.legal_role") : "",
  ].filter(Boolean);
  const evidenceStatus = readStatus(
    allAttachments.length > 0,
    attachmentsLoading,
    attachmentsError,
  );

  records.push({
    id: "company-profile",
    title: t("eudamed.item.company.title"),
    detail: profileError
      ? t("eudamed.profile_error")
      : profileLoading
        ? t("eudamed.profile_loading")
        : profileReady
          ? t("eudamed.item.company.detail_role_saved")
          : listMissingFields(t, missingCompanyFields),
    status: readStatus(profileReady, profileLoading, profileError),
    statusLabel: t(`eudamed.status.${readStatus(profileReady, profileLoading, profileError)}`),
    actionLabel: profileLoading || profileError ? "" : t("eudamed.open_section"),
    onAction: profileLoading || profileError ? undefined : onOpenCompany,
  });

  records.push({
    id: "project-devices",
    title: t("eudamed.item.devices.title"),
    detail: deviceList.length
      ? t(deviceList.length === 1
        ? "eudamed.item.devices.detail_count_one"
        : "eudamed.item.devices.detail_count_many").replace("{count}", String(deviceList.length))
      : t("eudamed.item.devices.detail_empty"),
    status: deviceList.length ? "recorded" : "missing",
    statusLabel: t(`eudamed.status.${deviceList.length ? "recorded" : "missing"}`),
    actionLabel: deviceList.length ? "" : t("eudamed.open_section"),
    onAction: deviceList.length ? undefined : onOpenDevice,
  });

  if (!["manufacturer", "importer", "distributor"].includes(roleView)) {
    records.push({
      id: "project-role",
      title: t("eudamed.item.project_role.title"),
      detail: t("eudamed.item.project_role.detail"),
      status: "missing",
      statusLabel: t("eudamed.status.missing"),
      actionLabel: t("eudamed.open_section"),
      onAction: onOpenProjectRole,
    });
  }

  deviceList.forEach((device) => {
    const deviceAttachments = attachmentsByDevice[device.id] || [];
    const manufacturerRecorded = hasText(device.manufacturer_name || device.manufacturer);
    const identityRecorded = hasText(device.name) && hasText(device.model);
    const udiRecorded = hasText(device.udi_di) && hasText(device.basic_udi_di);
    const variants = Array.isArray(device.variants) ? device.variants : [];
    const variantsHaveUdi = variants.every((item) => hasText(item?.udi_di));
    const udiComplete = udiRecorded && variantsHaveUdi;
    const missingIdentityFields = [
      !hasText(device.name) ? t("eudamed.field.product_name") : "",
      !hasText(device.model) ? t("eudamed.field.model") : "",
    ].filter(Boolean);
    const missingUdiFields = [
      !hasText(device.basic_udi_di) ? t("eudamed.field.basic_udi") : "",
      !hasText(device.udi_di) ? t("eudamed.field.udi") : "",
      ...variants.filter((item) => !hasText(item?.udi_di)).map((item, index) => (
        `${t("eudamed.field.variant_udi")} ${item?.name || index + 1}`
      )),
    ].filter(Boolean);
    const ceAttachment = attachmentFor(deviceAttachments, "ce_label");
    const declarationAttachment = attachmentFor(deviceAttachments, "signed_declaration");
    const deviceDetail = [
      device.model,
      device.mdr_class ? `MDR ${device.mdr_class}` : "",
    ].filter(Boolean).join(" · ");

    records.push({
      id: `identity-${device.id}`,
      title: title(t, "eudamed.item.device_identity.title", device.name || t("eudamed.unnamed_device")),
      detail: identityRecorded
        ? `${deviceDetail || t("eudamed.item.device_identity.detail")} · ${t("eudamed.item.device_identity.detail_stored")}`
        : listMissingFields(t, missingIdentityFields),
      status: identityRecorded ? "review" : "missing",
      statusLabel: t(`eudamed.status.${identityRecorded ? "review" : "missing"}`),
      actionLabel: t("eudamed.open_section"),
      onAction: () => onOpenDevice(device.id),
    });

    if (roleView === "importer" || roleView === "distributor") {
      const operatorReview = device.operator_review || {};
      const operatorStatus = operatorReview.status === "reviewed"
        ? "recorded"
        : operatorReview.status === "discrepancy" || operatorReview.status === "not_reviewed"
          ? "review"
          : "missing";
      records.push({
        id: `operator-review-${device.id}`,
        title: title(t, "eudamed.item.operator_review.title", device.name || t("eudamed.unnamed_device")),
        detail: operatorReview.status === "reviewed" || operatorReview.status === "discrepancy"
          ? t("eudamed.item.operator_review.detail_saved")
          : t("eudamed.item.operator_review.detail"),
        status: operatorStatus,
        statusLabel: t(`eudamed.status.${operatorStatus}`),
        actionLabel: t("eudamed.open_section"),
        onAction: () => onOpenTab("operator-review"),
      });
      records.push({
        id: `manufacturer-${device.id}`,
        title: title(t, "eudamed.item.manufacturer.title", device.name || t("eudamed.unnamed_device")),
        detail: manufacturerRecorded
          ? `${device.manufacturer_name || device.manufacturer} · ${t("eudamed.item.manufacturer.detail_stored")}`
          : t("eudamed.item.manufacturer.detail"),
        status: manufacturerRecorded ? "review" : "missing",
        statusLabel: t(`eudamed.status.${manufacturerRecorded ? "review" : "missing"}`),
        actionLabel: t("eudamed.open_section"),
        onAction: () => onOpenDevice(device.id),
      });
    }

    records.push({
      id: `udi-${device.id}`,
      title: title(t, "eudamed.item.udi.title", device.name || t("eudamed.unnamed_device")),
      detail: udiComplete
        ? (device.eudamed_registration_status === "registered" && hasText(device.eudamed_di_number)
          ? t("eudamed.item.udi.detail_registration_declared")
          : t("eudamed.item.udi.detail_assigned"))
        : listMissingFields(t, missingUdiFields),
      status: udiComplete ? "review" : (udiRecorded ? "review" : "missing"),
      statusLabel: t(`eudamed.status.${udiComplete || udiRecorded ? "review" : "missing"}`),
      actionLabel: t("eudamed.open_section"),
      onAction: () => onOpenDevice(device.id),
      downloadLabel: t("eudamed.download"),
      onDownload: () => onDownload({ id: `udi-${device.id}`, type: "udi", deviceId: device.id }),
    });

    records.push(attachmentItem({
      device,
      attachment: ceAttachment,
      recordId: "ce_label",
      t,
      onDownload,
      onAction: () => onOpenTab("ce-marking"),
      attachmentsLoading,
      attachmentsError,
    }));
    records.push(attachmentItem({
      device,
      attachment: declarationAttachment,
      recordId: "signed_declaration",
      t,
      onDownload,
      onAction: () => onOpenTab("ce-marking"),
      attachmentsLoading,
      attachmentsError,
    }));

    if (roleView !== "manufacturer") {
      const ceReview = device.ce_marking?.review_status;
      const ceReviewed = ceReview === "reviewed";
      const ceStatus = ceReviewed ? "recorded" : ceReview === "review" ? "review" : "missing";
      const roleReviewKey = roleView === "importer" ? "importer_review" : "distributor_review";
      records.push({
        id: `ce-review-${device.id}`,
        title: title(t, `eudamed.item.${roleReviewKey}.title`, device.name || t("eudamed.unnamed_device")),
        detail: ceReviewed
          ? t(`eudamed.item.${roleReviewKey}.detail_reviewed`)
          : t(`eudamed.item.${roleReviewKey}.detail`),
        status: attachmentsError ? "info" : ceStatus,
        statusLabel: t(`eudamed.status.${attachmentsError ? "info" : ceStatus}`),
        actionLabel: t("eudamed.open_section"),
        onAction: () => onOpenTab("ce-marking"),
        downloadLabel: t("eudamed.download"),
        onDownload: () => onDownload({
          id: `ce-review-${device.id}`,
          type: "operator_evidence",
          deviceId: device.id,
          roleView,
        }),
      });
    }
    deviceAttachments
      .filter((item) => !["ce_label", "signed_declaration"].includes(item.record_id))
      .forEach((attachment) => {
        const reviewed = attachment.review_status === "reviewed";
        const status = attachmentsError || attachmentsLoading
          ? "info"
          : reviewed ? "recorded" : "review";
        records.push({
          id: `attachment-${attachment.id}`,
          title: attachment.file_name,
          detail: `${attachment.module} · ${t(reviewed ? "eudamed.file_reviewed" : "eudamed.file_not_reviewed")}`,
          status,
          statusLabel: t(`eudamed.status.${status}`),
          downloadLabel: t("eudamed.download"),
          onDownload: () => onDownload({
            type: "attachment",
            deviceId: device.id,
            attachmentId: attachment.id,
            fileName: attachment.file_name,
          }),
        });
      });
  });

  if (roleView === "manufacturer") {
    const classificationPresent = hasText(version?.classification?.suggested_class);
    const riskRows = version?.risk?.rows || version?.risk?.hazards || [];
    const riskPresent = Array.isArray(riskRows) && riskRows.length > 0;
    const gsprState = version?.gspr_state || {};
    const savedGspr = Object.values(gsprState).filter((item) => (
      item && typeof item === "object" && (
        hasText(item.status) || hasText(item.evidence)
      )
    )).length;
    const clinicalPresent = nonEmptyRecord(version?.clinical);
    const intake = version?.intake || {};
    const identitySource = intake.device_identity || {};
    const purposeSource = intake.intended_purpose || {};
    const technicalFilePresent = hasText(identitySource.name) || hasText(purposeSource.description);
    const versionRecords = [
      {
        id: "classification",
        key: "classification",
        present: classificationPresent,
        missingDetail: "eudamed.item.classification.detail_missing",
        download: { type: "document", documentType: "classification" },
        tab: "classification",
      },
      {
        id: "risk",
        key: "risk",
        present: riskPresent,
        missingDetail: "eudamed.item.risk.detail_missing",
        download: { type: "document", documentType: "risk" },
        tab: "risk",
      },
      {
        id: "gspr",
        key: "gspr",
        present: savedGspr > 0,
        detail: savedGspr
          ? `${t("eudamed.item.gspr.detail_count").replace("{count}", String(savedGspr))} ${t("eudamed.item.gspr.detail_present_review")}`
          : t("eudamed.item.gspr.detail"),
        download: { type: "gspr" },
        tab: "gspr",
      },
      {
        id: "clinical",
        key: "clinical",
        present: clinicalPresent,
        missingDetail: "eudamed.item.clinical.detail_missing",
        download: { type: "document", documentType: "clinical" },
        tab: "clinical",
      },
      {
        id: "technical-file",
        key: "technical_file",
        present: technicalFilePresent,
        missingDetail: "eudamed.item.technical_file.detail_missing",
        download: { type: "document", documentType: "technical_file" },
        tab: "techfile",
      },
    ];
    versionRecords.forEach((item) => {
      const status = version ? (item.present ? "review" : "missing") : "info";
      records.push({
        id: item.id,
        title: t(`eudamed.item.${item.key}.title`),
        detail: version
          ? (item.detail || t(item.present ? `eudamed.item.${item.key}.detail_present_review` : item.missingDetail))
          : t("eudamed.version_unavailable"),
        status,
        statusLabel: t(`eudamed.status.${status}`),
        actionLabel: version ? t("eudamed.open_section") : "",
        onAction: version ? () => onOpenTab(item.tab) : undefined,
        downloadLabel: t("eudamed.download"),
        onDownload: version ? () => onDownload(item.download) : undefined,
      });
    });
  }

  if (roleView !== "manufacturer" && !deviceList.length) {
    records.push({
      id: "operator-evidence",
      title: t("eudamed.item.operator_evidence.title"),
      detail: attachmentsError ? t("eudamed.attachments_error") : t("eudamed.item.operator_evidence.detail"),
      status: readStatus(false, attachmentsLoading, attachmentsError),
      statusLabel: t(`eudamed.status.${readStatus(false, attachmentsLoading, attachmentsError)}`),
    });
  }

  if (attachmentsError && evidenceStatus === "info") {
    records.push({
      id: "attachments-load-error",
      title: t("eudamed.item.attachments.title"),
      detail: t("eudamed.attachments_error"),
      status: "info",
      statusLabel: t("eudamed.status.info"),
    });
  }

  return records;
}
